import { useEffect } from 'react';
import type { DocumentGenerationProgressDTO } from '@shared/schemas/document-generation';

// Mémoire de module : la carte (liste virtualisée) et le canvas montrent la même génération, et la
// carte peut se remonter en cours de route. Bornée : seules les générations récentes comptent.
const MAX_TRACKED_GENERATIONS = 100;
const generationsThatResearched = new Set<string>();

function rememberResearch(generationId: string) {
  generationsThatResearched.delete(generationId);
  generationsThatResearched.add(generationId);
  if (generationsThatResearched.size > MAX_TRACKED_GENERATIONS) {
    const oldest = generationsThatResearched.values().next().value;
    if (oldest) {
      generationsThatResearched.delete(oldest);
    }
  }
}

/**
 * Le statut n'annonce pas d'avance si une recherche web aura lieu : `progress.stage === 'research'` n'est
 * visible que pendant ~12 s de polling. Signal retenu pour afficher l'étape « Recherche » : le stage
 * `research` est courant, ou il a déjà été observé pour cette génération (mémorisé au fil du polling).
 * Le statut ne porte aucune information sur les sources web : une génération qui a sauté la recherche
 * (ou dont le stage a été manqué entre deux polls) garde simplement 4 étapes.
 */
export function useResearchStageSeen(
  generationId: string | undefined,
  progress: DocumentGenerationProgressDTO | undefined,
): boolean {
  const isResearching = progress?.stage === 'research';
  useEffect(() => {
    if (generationId && isResearching) {
      rememberResearch(generationId);
    }
  }, [generationId, isResearching]);
  return isResearching || Boolean(generationId && generationsThatResearched.has(generationId));
}
